// Performance Tier Badge Component for Creative Library
import React from 'react';
import type { PerformanceTier } from '../types/creative-library';

interface PerformanceTierBadgeProps {
  tier: PerformanceTier;
  roas?: number;
  totalCreatives?: number;
  className?: string;
  iconOnly?: boolean;
}

const tierConfig: Record<PerformanceTier, {
  icon: string;
  label: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  top: {
    icon: '🏆',
    label: 'Top 10%',
    bgColor: 'bg-success bg-opacity-10',
    textColor: 'text-success',
    borderColor: 'border-success'
  },
  above_avg: {
    icon: '⭐',
    label: 'Above Average',
    bgColor: 'bg-warning bg-opacity-10',
    textColor: 'text-warning',
    borderColor: 'border-warning'
  },
  average: {
    icon: '📊',
    label: 'Average',
    bgColor: 'bg-bodydark1 bg-opacity-10',
    textColor: 'text-bodydark1',
    borderColor: 'border-bodydark1'
  },
  below_avg: {
    icon: '⚠️',
    label: 'Below Average',
    bgColor: 'bg-orange-500 bg-opacity-10',
    textColor: 'text-orange-500',
    borderColor: 'border-orange-500'
  },
  poor: {
    icon: '🔴',
    label: 'Poor',
    bgColor: 'bg-danger bg-opacity-10',
    textColor: 'text-danger',
    borderColor: 'border-danger'
  }
};

export const PerformanceTierBadge: React.FC<PerformanceTierBadgeProps> = ({
  tier,
  roas,
  totalCreatives,
  className = '',
  iconOnly = false
}) => {
  const config = tierConfig[tier];
  
  const tooltipText = React.useMemo(() => {
    if (!roas) return config.label;
    
    let text = `ROAS: ${roas.toFixed(2)}x`;
    
    if (tier === 'top' && totalCreatives) {
      text += ` (Top 10% of ${totalCreatives} creatives)`;
    } else if (tier === 'poor' && totalCreatives) {
      text += ` (Bottom 10% of ${totalCreatives} creatives)`;
    } else {
      text += ` (${config.label})`;
    }
    
    return text;
  }, [roas, tier, config.label, totalCreatives]);
  
  // Render icon-only version
  if (iconOnly) {
    return (
      <span
        className={`
          inline-flex items-center justify-center w-7 h-7 rounded-full
          ${config.bgColor} ${config.textColor} border ${config.borderColor}
          text-sm font-medium transition-all cursor-default
          hover:scale-110 hover:shadow-sm
          ${className}
        `}
        title={tooltipText}
      >
        {config.icon}
      </span>
    );
  }
  
  // Render full badge
  return (
    <div
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border
        ${config.bgColor} ${config.textColor} ${config.borderColor}
        text-xs font-medium transition-all cursor-default
        hover:scale-105 hover:shadow-sm
        ${className}
      `}
      title={tooltipText}
    >
      <span className="text-sm leading-none">{config.icon}</span>
      <span className="leading-none">{config.label}</span>
      {roas !== undefined && (
        <span className="leading-none opacity-75">
          {roas.toFixed(1)}x
        </span>
      )}
    </div>
  );
};

// Compact version for cards
export const PerformanceTierIcon: React.FC<PerformanceTierBadgeProps> = ({
  tier,
  roas,
  totalCreatives,
  className = ''
}) => {
  const config = tierConfig[tier];
  
  const tooltipText = React.useMemo(() => {
    if (!roas) return config.label;
    return `ROAS: ${roas.toFixed(2)}x (${config.label})`;
  }, [roas, tier, config.label]);
  
  return (
    <span
      className={`
        inline-flex items-center justify-center w-7 h-7 rounded-full
        ${config.bgColor} ${config.textColor} border ${config.borderColor}
        text-sm font-medium transition-all cursor-default
        hover:scale-110 hover:shadow-sm
        ${className}
      `}
      title={tooltipText}
    >
      {config.icon}
    </span>
  );
};
